import { useEffect, useRef } from "react";
import { createVideoSlots } from "../utils/videoSlots.js";

// App-wide bound on playing + loaded videos — see utils/videoSlots.js for why (a seller's grid of raw
// 10-45 MB videos exhausted phone memory: black screen / hang / browser killed, 2026-10-02).
const slots = createVideoSlots({ maxPlaying: 2, maxLoaded: 6 });

/**
 * Autoplaying product video that only downloads + plays while it is ON SCREEN.
 *
 * The storefront / marketplace grids used <video autoPlay loop preload="auto">, which
 * makes the browser download the ENTIRE video for every card — even ones the shopper
 * never scrolls to — and product videos are raw and uncompressed (up to 100 MB). That
 * was the runaway Bunny CDN egress: one autoplaying banner video × thousands of visits
 * = tens of GB from a single file.
 *
 * This defers everything to the viewport:
 *   • no `src` is set until the element enters view (IntersectionObserver), so a video
 *     the shopper never reaches costs ZERO bandwidth,
 *   • it plays when visible and pauses when it leaves (stops further buffering),
 *   • a poster still image keeps the card from flashing blank before load.
 * For anything actually on screen it looks identical to the old autoplay behaviour, except
 * that at most MAX_CONCURRENT_PLAYING are ever actually decoding at once (see above).
 */
export function AutoVideo({ src, poster, style, className }) {
  const ref = useRef(null);
  const loaded = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !src) return;
    const unload = () => {             // free the buffer + decoder, not just pause
      try { el.pause(); el.removeAttribute("src"); el.load(); } catch (_) {}
      loaded.current = false;
    };
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!loaded.current) {
          el.autoplay = true;          // native autoplay resumes cleanly when the tab regains focus
          el.src = src;                // load once, lazily — only now does any bandwidth get spent
          loaded.current = true;
        }
        slots.claim(el, unload);       // may pause/unload the oldest videos elsewhere on the page
        el.play?.().catch(() => {});   // belt-and-suspenders for re-entry after scrolling away
      } else {
        el.pause?.();                  // stop playing off-screen; the slot manager unloads it when it ages out
      }
    }, { threshold: 0.25, rootMargin: "150px 0px" });                  // warm up just before entry
    io.observe(el);
    return () => { io.disconnect(); slots.release(el); unload(); };
  }, [src]);

  return (
    <video
      ref={ref}
      poster={poster}
      muted loop playsInline
      preload="none"
      className={className}
      style={{ background: "#efece8", ...style }}   // neutral, never a black flash while a card has no poster / is unloaded
    />
  );
}
